import React from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, ShieldCheck } from 'lucide-react'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-[#f4f1ea] text-stone-700 border-t border-stone-200">
      {/* Upper Footer: 3-column layout */}
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 grid gap-12 md:grid-cols-3">
        {/* Column 1: Brand Wordmark & Description */}
        <div className="space-y-6">
          <Link to="/" className="flex flex-col group">
            <span className="font-serif text-xl tracking-widest text-[#1c2b21] uppercase font-light leading-none">
              CRAFT CAFÉ
            </span>
            <span className="text-[9px] font-sans font-light uppercase tracking-[0.3em] text-[#b89a6c] mt-1">
              café & catering
            </span>
          </Link>
          <p className="text-xs text-stone-600 leading-relaxed font-light max-w-sm">
            Vi leverer velsmagende, hjemmelavet mad, tapas, kager, brød og professionel catering til alle typer selskaber i Horsens og omegn. Altid lavet helt fra bunden med fokus på de gode råvarer.
          </p>
        </div>

        {/* Column 2: Kontakt os */}
        <div className="space-y-6">
          <h4 className="font-serif text-sm tracking-widest text-[#b89a6c] uppercase font-light">Kontakt os</h4>
          <ul className="space-y-4 text-xs font-light">
            <li className="flex items-start gap-3">
              <Phone size={12} className="text-[#b89a6c] mt-0.5 shrink-0" />
              <div>
                <p className="text-stone-500 uppercase text-[9px] tracking-widest">Ring direkte</p>
                <a href="tel:+45489981" className="text-stone-700 hover:text-[#b89a6c] transition-colors whitespace-nowrap font-medium">
                  45 48 99 81
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={12} className="text-[#b89a6c] mt-0.5 shrink-0" />
              <div>
                <p className="text-stone-500 uppercase text-[9px] tracking-widest">Send e-mail</p>
                <a href="mailto:craft.line25@gmail.com" className="text-stone-700 hover:text-[#b89a6c] transition-colors whitespace-nowrap font-medium">
                  craft.line25@gmail.com
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={12} className="text-[#b89a6c] mt-0.5 shrink-0" />
              <div>
                <p className="text-stone-500 uppercase text-[9px] tracking-widest">Adresse</p>
                <p className="text-stone-700 leading-relaxed">
                  Vejlevej 10, 8700 Horsens
                </p>
              </div>
            </li>
          </ul>
        </div>

        {/* Column 3: Åbningstider */}
        <div className="space-y-6">
          <h4 className="font-serif text-sm tracking-widest text-[#b89a6c] uppercase font-light">Åbningstider</h4>
          <ul className="space-y-4 text-xs font-light">
            <li className="flex items-start gap-3">
              <Clock size={12} className="text-[#b89a6c] mt-0.5 shrink-0" />
              <div>
                <p className="text-stone-500 uppercase text-[9px] tracking-widest">Mandag - Fredag</p>
                <p className="text-stone-700">Kl. 08.00 - 17.00</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={12} className="text-[#b89a6c] mt-0.5 shrink-0" />
              <div>
                <p className="text-stone-500 uppercase text-[9px] tracking-widest">Lørdag - Søndag</p>
                <p className="text-stone-700">Efter aftale</p>
              </div>
            </li>
            <li className="text-[10px] text-[#b89a6c] border border-[#b89a6c]/20 bg-[#b89a6c]/5 px-4 py-3 font-light leading-relaxed">
              <span className="font-semibold">Bemærk:</span> Vi leverer mad ud af huset alle ugens dage efter forudgående aftale!
            </li>
          </ul>
        </div>
      </div>

      {/* Lower Footer: Tier 2 Utility Bar */}
      <div className="border-t border-stone-200 bg-[#ebe7df] py-8">
        <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Left: CVR and Copyright */}
          <div className="text-[10px] uppercase tracking-widest text-stone-500 text-center md:text-left space-y-2 font-light">
            <div>
              <p>© {currentYear} Craft Café. Alle rettigheder forbeholdes.</p>
              <p>CVR: 45489981 • Vejlevej 10, 8700 Horsens</p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-1 text-stone-400">
              <a 
                href="https://www.iubenda.com/privacy-policy/34672321/cookie-policy" 
                className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-[#b89a6c] transition-colors" 
                title="Cookiepolitik "
              >
                Cookiepolitik
              </a>
              <span className="text-stone-300 hidden sm:inline">•</span>
              <a 
                href="https://www.iubenda.com/privacy-policy/34672321" 
                className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-[#b89a6c] transition-colors" 
                title="Privatlivspolitik "
              >
                Privatlivspolitik
              </a>
            </div>
          </div>

          {/* Right: Flat navigation links & small CTA */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <Link to="/" className="text-[10px] uppercase tracking-widest text-stone-600 hover:text-[#b89a6c] transition-colors font-light">Forside</Link>
            <Link to="/mad-ud-af-huset" className="text-[10px] uppercase tracking-widest text-stone-600 hover:text-[#b89a6c] transition-colors font-light">Mad ud af huset</Link>
            <Link to="/catering" className="text-[10px] uppercase tracking-widest text-stone-600 hover:text-[#b89a6c] transition-colors font-light">Catering</Link>
            <Link to="/tapas" className="text-[10px] uppercase tracking-widest text-stone-600 hover:text-[#b89a6c] transition-colors font-light">Tapas</Link>
            <Link to="/kager-og-brod" className="text-[10px] uppercase tracking-widest text-stone-600 hover:text-[#b89a6c] transition-colors font-light">Kager & Brød</Link>
            <Link to="/kontakt" className="bg-[#b89a6c] text-white px-4 py-2 text-[9px] font-semibold uppercase tracking-widest hover:bg-[#1c2b21] transition-colors rounded-none">Kontakt</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
